
import moment from 'moment';
import { mapState, mapMutations } from 'vuex';

import DefaultForm from '../_globals/forms/DefaultForm.vue';
import Icon from './Icon'

export default {
  components: { DefaultForm, Icon },
  name: 'location-reservation',
  data() {
    return {
      selectedPartySize: null,
      selectedDate: null,
      selectedTime: null,
      timeOptions: this.generateTimeOptions(),
      availability: [],
      showReservationFields: false,
      selectedItem: null,
      selectedSlot: null,
      reservationNotes: '',
      reservationFields: [
        {
          name: 'name',
          label: 'Name',
          placeholder: 'Enter Full Name',
          value: '',
          type: 'text',
          required: true,
          valid: /[A-Za-z]+/,
          errors: {
            invalid: 'A full name is required'
          }
        },
        {
          name: 'phone',
          label: 'Phone',
          value: '',
          placeholder: '000-000-0000',
          valid: /^(?:\+1\s?)?\(?([2-9][0-8][0-9])\)?[-. ]?([2-9][0-9]{2})[-. ]?([0-9]{4})$/,
          mask: '###-###-####',
          touched: false,
          required: true,
          error: null,
          errors: {
            invalid: 'A valid phone number is required.'
          }
        },
        {
          name: 'email',
          label: 'Email',
          type: 'email',
          placeholder: 'Enter Email Address',
          value: '',
          valid: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          touched: false,
          required: true,
          error: null,
          errors: {
            invalid: 'Please enter a valid email address.'
          }
        },
      ],
      reservationResponse: null,
    }
  },
  computed: {
    ...mapState(['location', 'locations']),
    canSendRequest() {
      return Boolean(this.selectedPartySize) 
          && Boolean(this.selectedDate)
          && Boolean(this.selectedTime)
    },
    showReservationForm() {
      return this.availability?.length === 0
    },
    formattedSelectedDate() {
      return moment(this.selectedDate).format('ddd MMMM D');
    },
    formattedSelectedTime() {
      // Remove the "T" and the offset part from the original time string
      const cleanedTime = this.selectedTime.replace('T', '');
      // Parse the cleaned time string using moment.js
      const parsedTime = moment(cleanedTime, 'HH:mm:ss');
      // Format the parsed time in the desired format
      const formattedTime = parsedTime.format('hh:mm A');
      return formattedTime;
    },
    formattedSelectedSlotTime() {
      if(this.selectedSlot?.timestamp) {
        return moment(this.selectedSlot.timestamp).utc().format();
      }
    },
    showSelectedTime() {
      return Boolean(this.selectedTime) && !Boolean(this.selectedSlot?.timestamp);
    },
    showReservationInfo() {
      return Boolean(this.reservationResponse);
    }
  },
  methods: {
    ...mapMutations([ 'setLoading', 'setErrors' ]),
    async getReservationSlots() {
      const config = {
        method: 'get',
        url: `reservations/slots`,
        params: {
          location_id: this.location.wisely_id,
          party_size: this.selectedPartySize,
          search_window_minutes: 180,
          ts: this.selectedDate + this.selectedTime,
        }
      }
      const response = await this.$store.dispatch('sendRequest', config, { root: true });
      if (response.status === 'ok') {
        this.availability = response.data.availability_types;
        return true
      } else {
        let messages = []
        if (response.errors.length) {
          messages = [response.errors[0].message]
        } else if (typeof response.message === 'string') {
          messages.push(response.message)
        } else if (Array.isArray(response.message)) {
          messages = [...response.message]
        } else {
          messages = response.message
        }
        this.setLoading(false);
        this.$store.commit('setErrors', messages, { root: true })
      }
    },

    dateFromNow(daysToAdd) {
      const futureDate = moment().add(daysToAdd, 'days');
      const formattedDate = futureDate.format('YYYY-MM-DD');
      const formattedLabel = futureDate.format('ddd, MMM. D');

      return {
        timestamp: formattedDate,
        label: formattedLabel
      };
    },

    generateTimeOptions() {
      const startTime = moment('11:00 AM', 'h:mm A');
      const endTime = moment('10:00 PM', 'h:mm A');
      const timeOptions = [];

      while (startTime.isBefore(endTime)) {
        timeOptions.push(moment(startTime));
        startTime.add(15, 'minutes');
      }

      return timeOptions;
    },
    
    formatTime(time, format='THH:mm:ss') {
      // Format selected time with local user UTC offset
      const formattedTime = time.format(format) + moment().format('Z');
      return formattedTime;
    },

    resetReservationForm() {
      this.selectedTime = null;
      this.availability = [];
    },

    formattedSlotTime(time) {
      // Parse the original time string using moment.js and consider the original timezone offset
      const parsedTime = moment(time).utcOffset(moment().utcOffset());

      // Format the parsed time in the desired format
      const formattedTime = parsedTime.format('hh:mm A');

      return formattedTime;
    },

    setReservation(slot, item) {
      this.selectedSlot = slot;
      this.selectedItem = item;
      this.showReservationFields = true;
    },
    
    async preSubmitReservation(data) {
      this.setLoading(true)
      // handling phone number format for third party API 
      data.phone = `+1${data.phone.replace(/\-/g, '')}`
      await this.submitReservation(data)
      this.setLoading(false)
    },

    async submitReservation(data) {
      const config = {
        method: 'post',
        url: 'reservations',
        data: Object.assign({}, data, {
          location_id: this.location.wisely_id,
          party_size: this.selectedPartySize,
          availability_type_id: this.selectedItem.availability_type_id,
          reserved_at: this.selectedSlot.timestamp,  
          notes: this.reservationNotes,
        })
      }
      const response = await this.$store.dispatch('sendRequest', config, { root: true });
      this.setLoading(false)
      if (response.status === 'ok') {
        this.availability = response.data.availability_types;
        this.reservationResponse = response.data
        return true
      } else {
        let messages = []
        if (response.errors?.length) {
          messages = [response.errors[0].message]
        } else if (typeof response.message === 'string') {
          messages.push(response.message)
        } else if (Array.isArray(response.message)) {
          messages = [...response.message]
        } else {
          messages = response.message
        }
        this.$store.commit('setErrors', messages, { root: true })
      }
    }
  }
}
